/* global runtime */
const logAiSuggestionOutcome = async (body = {}) => {
  if (!runtime.configured || !runtime.auth?.currentUser) return null;
  try {
    return await authorizedPost('logAiSuggestionOutcomeHttp', body);
  } catch (error) {
    if (Number(error?.status) === 404 && (error?.code === 'not-found' || /AI suggestion log was not found/i.test(String(error?.message || '')))) {
      return { ok: true, skipped: 'suggestion-log-not-found' };
    }
    console.warn('Kunne ikke lagre AI-fasit.', error);
    return null;
  }
};

const queueAiLearningLog = (run, context = {}) => {
  const attempt = () => Promise.resolve().then(run).then((result) => {
    if (result == null && runtime.configured) throw new Error('AI learning log was not persisted.');
    return result;
  });
  void attempt().catch(() => {
    window.setTimeout(() => {
      void attempt().catch((error) => {
        console.warn('Kunne ikke lagre AI-fasit etter nytt forsok.', error);
        window.dispatchEvent(new CustomEvent('plumme-ai-learning-log-failed', { detail: { ...context, message: error?.message || String(error) } }));
      });
    }, 750);
  });
};

const bankLineFinalChoiceForAiLearning = (line = {}) => {
  const raw = line._backend || line;
  return stripUndefinedDeep({
    lineId: raw.id || line.id,
    classification: raw.classification || line.classification,
    bookingType: raw.bookingType || line.bookingType,
    accountCode: raw.accountCode || raw.fromAccountCode || line.accountCode,
    vatRate: raw.vatRate ?? raw.fromVatRate ?? line.vatRate,
    reverseChargeCode: raw.reverseChargeCode || (raw.reverseCharge ? '86' : ''),
    matchedEntryType: raw.matchedEntryType || line.matchedEntryType,
    receiptInboxEntryId: raw.receiptInboxEntryId || line.receiptInboxEntryId,
  });
};

const BANK_LINE_DRAFT_LEARNING_FIELDS = [
  'classification',
  'bookingType',
  'accountCode',
  'fromAccountCode',
  'toAccountCode',
  'vatRate',
  'fromVatRate',
  'toVatRate',
  'reverseChargeCode',
  'matchedEntryType',
  'receiptInboxEntryId',
];

const bankLineDraftCorrectionForAiLearning = (lineId, patch = {}) => {
  const changedFields = BANK_LINE_DRAFT_LEARNING_FIELDS.filter((field) =>
    Object.prototype.hasOwnProperty.call(patch || {}, field),
  );
  if (!changedFields.length) return null;
  const finalChoice = bankLineFinalChoiceForAiLearning({ id: lineId, ...patch });
  if (!Object.keys(finalChoice || {}).some((key) => key !== 'lineId')) return null;
  return stripUndefinedDeep({
    source: 'bank_line_editor',
    changedFields,
    finalChoice,
  });
};

const receiptLearningValue = (value) => (
  value === undefined || value === null || value === '' ? null : value
);

const RECEIPT_DRAFT_LEARNING_FIELDS = [
  ['documentType', 'documentType'],
  ['suggestedPostingType', 'postingType'],
  ['suggestedPaymentSource', 'paymentSource'],
  ['paymentAccountCode', 'paymentAccountCode'],
  ['receiptDate', 'receiptDate'],
  ['dueDate', 'dueDate'],
  ['isPaid', 'isPaid'],
  ['totalGrossNok', 'totalGrossNok'],
  ['vatRate', 'vatRate'],
  ['supplierName', 'supplierName'],
  ['invoiceNumber', 'invoiceNumber'],
  ['description', 'description'],
  ['suggestedCostAccountCode', 'accountCode'],
  ['reverseChargeCode', 'reverseChargeCode'],
];

const receiptDraftLearningFinalChoice = (analysis = {}) => stripUndefinedDeep(Object.fromEntries(
  RECEIPT_DRAFT_LEARNING_FIELDS.map(([analysisField, finalField]) => [finalField, analysis?.[analysisField]]),
));

const receiptLearningChangedFields = (beforeAnalysis = {}, afterAnalysis = {}) =>
  RECEIPT_DRAFT_LEARNING_FIELDS
    .filter(([analysisField]) => JSON.stringify(receiptLearningValue(beforeAnalysis?.[analysisField]))
      !== JSON.stringify(receiptLearningValue(afterAnalysis?.[analysisField])))
    .map(([analysisField]) => analysisField);

const logBankSuggestionOutcome = async ({ statementId, lines = [], outcome = 'booked' } = {}) =>
  logAiSuggestionOutcome({
    sourceType: 'bank_line',
    sourceId: statementId,
    outcome,
    finalUserChoice: {
      source: 'business',
      bookedAt: nowIso(),
      lines: safeArray(lines).map(bankLineFinalChoiceForAiLearning).filter(Boolean),
    },
  });

const logBankLineDraftCorrection = async ({ statementId, lineId, patch } = {}) => {
  const sourceId = String(statementId || '').trim();
  const correction = bankLineDraftCorrectionForAiLearning(lineId, patch);
  if (!sourceId || !correction) return null;
  return logAiSuggestionOutcome({
    sourceType: 'bank_line',
    sourceId,
    outcome: 'draft_correction',
    draftCorrection: correction,
  });
};

const logReceiptSuggestionOutcome = async ({ entry, finalChoice, outcome = 'booked' } = {}) => {
  const sourceId = String(entry?.id || finalChoice?.receiptInboxEntryId || '').trim();
  if (!sourceId) return null;
  return logAiSuggestionOutcome({
    sourceType: 'receipt_inbox',
    sourceId,
    outcome,
    finalUserChoice: stripUndefinedDeep(finalChoice || {}),
  });
};

const logReceiptDraftCorrection = async ({ entry, beforeAnalysis, afterAnalysis, changedFields, source = 'receipt_suggestion_panel' } = {}) => {
  const sourceId = String(entry?.id || '').trim();
  const fields = safeArray(changedFields).length ? safeArray(changedFields) : receiptLearningChangedFields(beforeAnalysis, afterAnalysis);
  if (!sourceId || !fields.length) return null;
  return logAiSuggestionOutcome({
    sourceType: 'receipt_inbox',
    sourceId,
    outcome: 'draft_correction',
    draftCorrection: stripUndefinedDeep({
      source,
      changedFields: fields,
      finalChoice: receiptDraftLearningFinalChoice(afterAnalysis || {}),
    }),
  });
};
